import { inject, Inject, Injectable } from '@angular/core';
import { IRequestCallback, IRequestCallbackFunction, RequestService } from '@df/api/request/request.service';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { DfEventService } from '@df/core/service/df-event';
import { Status } from '@df/core/status';
import { TimeoutService } from '@df/ng/timeout.service';
import { BasketItem } from '@df/session/api/basket/basket-item';
import { BasketItemFactory } from '@df/session/api/basket/basket-item.factory';
import { BasketService } from '@df/session/api/basket/basket.service';
import { Customer } from '@df/session/api/customer';
import { NSession } from '@df/session/df-session.config';
import { PriceService, TaxArea, TaxMode } from '@df/ui/price/price.service';
import { Nexus } from '@tomandco/nexus-api-types';
import cloneDeep from 'lodash-es/cloneDeep';
import each from 'lodash-es/each';
import extend from 'lodash-es/extend';
import find from 'lodash-es/find';
import isArray from 'lodash-es/isArray';
import isEqual from 'lodash-es/isEqual';
import isObject from 'lodash-es/isObject';
import map from 'lodash-es/map';
import omit from 'lodash-es/omit';
import pickBy from 'lodash-es/pickBy';
import reduce from 'lodash-es/reduce';
import orderBy from 'lodash-es/orderBy';
import uniq from 'lodash-es/uniq';
import values from 'lodash-es/values';
import { Subject, Subscription } from 'rxjs';
import { BasketDataChangeEvent } from './basket-data-change-event';
import { DfApiConfigService } from '@df/api/config/df-api-config.service';
import { Prefs } from '@df/session/prefs';

/**
 * @deprecated Use Nexus.Lib.Basket.BasketData instead
 */
export interface IBasketData extends Nexus.Lib.Basket.BasketData {}

/**
 * @deprecated Use Nexus.Lib.Basket.Totals instead
 */
export interface IBasketTotals extends Nexus.Lib.Basket.Totals {}

/**
 * @deprecated Use Nexus.Lib.Basket.BuyRequest instead
 */
export interface IBuyRequestData extends Nexus.Lib.Basket.BuyRequest {}

/**
 * @deprecated Use Nexus.Lib.Basket.AddToBasketData instead
 */
export interface IAddToBasketData extends IBuyRequestData, Nexus.Lib.Basket.AddToBasketData {}

declare module '@df/core/service/df-event' {
    interface IEventType {
        // basket
        basketDataChange: DfEventConfig<'basketDataChange', BasketDataChangeEvent, void>;
    }
}
@Injectable({
    providedIn: 'root'
})
export class Basket {
    data: Nexus.Lib.Basket.BasketData<number, number> = {
        items: [],
        qty: 0,
        updated_at: ''
    };

    qty = 0;

    items: BasketItem[] = [];

    /**
     * @deprecated Use dfEventService with basketDataChange instead
     */
    readonly basketUpdateSubject = new Subject<void>();

    promise: Promise<Product[]> | undefined;

    private prefs = inject(Prefs);

    constructor(
        protected request: RequestService,
        protected basketService: BasketService,
        protected priceService: PriceService,
        protected app: App,
        protected customer: Customer,
        protected timeoutService: TimeoutService,
        readonly basketItemFactory: BasketItemFactory,
        protected dfEventService: DfEventService,
        @Inject(NSession.OPTIONS) private options: NSession.IModuleOptions
    ) {
        this.register();
        // if (data) {
        //     this.setData(data);
        // }
    }

    protected register() {
        this.customer.register(
            {
                id: 'basket',
                listener: 'cart',
                account: false
            },
            this
        );
    }

    byId(id: number): BasketItem | undefined {
        return find(this.items, item => item.id === id);
    }

    byProduct(id: number): BasketItem | undefined {
        return find(this.items, item => item.data && item.data.product === id);
    }

    /**
     * Finds basket item by addToBasket request
     */
    byAddToBasketRequest(request: Nexus.Lib.Basket.BasketRequestData): BasketItem | undefined {
        if (request) {
            const productId = request.id;
            const itemsByProduct = this.items.filter(item => item.data.product === productId);
            if (itemsByProduct.length === 1) {
                return itemsByProduct[0];
            }
            return this.filterItemsByRequest(itemsByProduct, request)[0];
        }

        return undefined;
    }

    filterItemsByRequest(items: BasketItem[], request: Nexus.Lib.Basket.BasketRequestData, valuesToOmit?: string[]): BasketItem[] {
        const normalizeRequest = (req: Nexus.Lib.Basket.BasketRequestData) => {
            const r = cloneDeep(req);
            if (r.options) {
                const product = this.app.resources.product.getExisting(r.id);
                if (product) {
                    const customOptions = product.customOptions;
                    if (customOptions?.length) {
                        each(customOptions, customOption => {
                            const type = customOption.type;
                            if (type === 'drop_down' || type === 'checkbox') {
                                const selectedValue = r.options[customOption.id];
                                if (typeof selectedValue === 'number') {
                                    r.options[customOption.id] = [selectedValue];
                                }
                            }
                        });
                    }
                }
            }
            const toOmit = ['qty', 'base_price', ...(valuesToOmit || [])];
            return pickBy(omit(r, toOmit), value => {
                if (!isArray(value) && isObject(value) && values(value).length === 0) {
                    return false;
                }
                return value !== undefined;
            }) as Nexus.Lib.Basket.BasketRequestData;
        };
        return items.filter(item => isEqual(normalizeRequest(item.data.request), normalizeRequest(request)));
    }

    addCoupon(status: Status | undefined, data, onSuccess?: IRequestCallback, onError?: IRequestCallback) {
        return this.request.send(status, () => this.request.nexus.coupon.add(data), onSuccess, onError);
    }

    formAddCoupon(form: any, status: Status, data, onSuccess?: IRequestCallback, onError?: IRequestCallback) {
        return this.request.submitForm(form, status, () => this.request.nexus.coupon.add(data), onSuccess, onError);
    }

    removeCoupon(status: Status, data, onSuccess?: IRequestCallback, onError?: IRequestCallback) {
        return this.request.send(status, () => this.request.nexus.coupon.remove(data), onSuccess, onError);
    }

    /**
     * Adds product to basket
     * You can use several types in request see param.
     * If number then will add product with that id and qty 1
     * If number then will add product with that string parsed to number as id and qty 1
     *
     * @param {addToBasketData | addToBasketData[] | number | string} addToBasketData
     */
    add(
        status,
        addToBasketData: Nexus.Lib.Basket.BasketRequestData | Nexus.Lib.Basket.BasketRequestData[] | number | string | undefined,
        onSuccess?: IRequestCallbackFunction,
        onError?: IRequestCallback
    ) {
        return this.basketService.add(status, addToBasketData, onSuccess, onError);
    }

    /**
     * Register observer
     * Observer function are called on basket update
     * @param  {() => void} callback
     */
    subscribe(...args): Subscription {
        return this.basketUpdateSubject.subscribe(...args);
    }

    private setDataPromise: Promise<any> | undefined;

    async setData(data: Nexus.Lib.Basket.BasketData) {
        if (!isEqual(data, this.data)) {
            this.setDataPromise = this.dfEventService.dispatch({
                id: 'basketDataChange',
                data: new BasketDataChangeEvent(this.customer.isLoaded ? this.data : undefined, data, this),
                action: async event => {
                    this.data = event.after!;

                    // sort items, newest first
                    if (data.items) {
                        data.items = orderBy(data.items, ['data.added_at'], ['desc']);
                    }

                    // load dependencies products
                    const productIds = this.getProductIdsToLoad(data);
                    if (productIds.length) {
                        await this.loadItemsDependecies(productIds);
                    }

                    const loadedProducts = this.app.resources.product.getAllExisting(productIds);
                    const childreIds: number[] = [];
                    loadedProducts.filter(i => i.isBundle).forEach(i => childreIds.push(...(i.childrenIds || [])));
                    if (childreIds.length) {
                        await this.app.resources.product.loadAllIds(childreIds, 3, { safe: true });
                    }

                    if (this.options.basket.loadProductParent) {
                        const parents = loadedProducts.map(i => (i.data as IProductDataV2).parent).filter(i => !!i) as number[];
                        if (parents) {
                            await this.app.resources.product.loadAllIds(parents, 3, { safe: true });
                        }
                    }
                    this.onDataUpdate(this.createItems(data.items));

                    const prefsInitPromise = this.prefs.init();
                    if (prefsInitPromise) {
                        await prefsInitPromise;
                    }
                    this.setDataPromise = undefined;
                }
            });
        }
        return this.setDataPromise;
    }

    onDataUpdate(items: BasketItem[]): void {
        this.items = items;
        this.qty = reduce(
            map(this.items, item => item.qty),
            (qty, total) => total + qty,
            0
        );
        this.basketUpdateSubject.next();
    }

    getProductIdsToLoad(data: Nexus.Lib.Basket.BasketData = this.data): number[] {
        return data ? uniq(map(data.items, item => item.product as number)) : [];
    }

    loadItemsDependecies(productIds: number[]): Promise<any> {
        return Promise.all(
            [this.app.resources.product.loadAllIds(productIds, 3, { safe: true }), this.app.resources.attribute.loadForProduct(2)].filter(
                i => !!i
            ) as Promise<any>[]
        );
    }

    protected createItems(itemsData: Nexus.Lib.Basket.BasketItem[]): BasketItem[] {
        return map(itemsData, itemData => this.basketItemFactory.create(itemData, this)) || [];
    }

    ///////////// TOTALS ////////////////////

    get subtotal(): number {
        return this.getSubtotal();
    }

    getSubtotal(taxMode: TaxMode = TaxMode.skip): number {
        return this.getTotal('subtotal', 'subtotal_tax', taxMode);
    }

    get discount(): number {
        return this.getDiscount();
    }

    getDiscount(taxMode: TaxMode = TaxMode.skip): number {
        return this.getTotal('discount', 'discount_tax', taxMode);
    }

    get shipping(): number {
        return this.getShipping();
    }

    getShipping(taxMode: TaxMode = TaxMode.skip): number {
        return this.getTotal('shipping', 'shipping_tax', taxMode, undefined, TaxArea.shipping);
    }

    getTotal(
        valueId: string,
        taxValueId: string,
        taxMode: TaxMode = TaxMode.skip,
        totals: Nexus.Lib.Basket.Totals | undefined = this.data?.totals,
        taxArea: TaxArea = TaxArea.basket
    ): number {
        if (totals && totals[valueId]) {
            const value = totals[valueId];
            return this.priceService.applyTax(Math.abs(value), -Math.abs(totals[taxValueId] || 0), taxMode, taxArea) * (value < 0 ? -1 : 1);
        }
        return 0;
    }

    get subtotalWithDiscount() {
        return this.subtotal + this.discount;
    }

    set totals(totals: Nexus.Lib.Basket.Totals) {
        if (!isEqual(this.data.totals, totals)) {
            this.data.totals = totals;
            this.basketUpdateSubject.next();
        }
    }

    get totals(): Nexus.Lib.Basket.Totals | undefined {
        return this.data?.totals;
    }

    ////////////////////////////

    /**
     * Checks if basket is salable
     */
    get isSalable(): boolean {
        return !this.getBasketError();
    }

    /**
     * Checks if there are only virtual products in the basket (no shipping)
     * @return {Boolean}
     */
    get isVirtual(): boolean {
        return !find(this.items, item => !item.data.virtual);
    }

    /**
     * Checks if basket is fully in stock (means that no backorders)
     */
    get isInStock(): boolean {
        return !find(this.items, item => !item.isInStock());
    }

    getBasketError(): string | undefined {
        if (this.data.error) {
            return this.data.error;
        }
        if (find(this.items, item => !item.isSalable())) {
            return this.getNonSalableItemsErrorMessage();
        }
        return undefined;
    }

    protected getNonSalableItemsErrorMessage(): string | undefined {
        return 'To proceed to checkout please remove out of stock products from your basket';
    }

    /**
     * Normalizes any allowed input data to standard interface
     * @param {addToBasketData | addToBasketData[] | number | string} addToBasketData
     * @return {addToBasketData} Normalized request data
     */
    normalizeAddToBasketData(
        inputData: Nexus.Lib.Basket.BasketRequestData | Nexus.Lib.Basket.BasketRequestData[] | number | string | undefined
    ): Nexus.Lib.Basket.BasketRequestData[] | undefined {
        return this.basketService.normalizeAddToBasketData(inputData);
    }

    toBuyRequest() {
        return this.data
            ? this.basketService.normalizeAddToBasketData(this.data.items.map(item => extend({ id: item.product }, item.request)))
            : [];
    }
}

/**
 * Register service in customer
 */
declare module '@df/session/api/customer' {
    interface Customer {
        basket: Basket;
    }
}
